import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import GenericTable from "../GenericTable/GenericTable";
import { CircularProgress, Alert, Snackbar, Select, MenuItem, FormControl, Box, Paper, Typography, Button } from "@mui/material";
import SyncIcon from '@mui/icons-material/Sync';
import config from "../../../config";

const ordersColumns = [
  {
    id: "order_id",
    label: "Order ID",
    render: (row) => row.order_id ?? "N/A"
  },
  {
    id: "product_names",
    label: "Product Name(s)",
    noWrap: false,
    maxWidth: "250px",
    render: (row) =>
      Array.isArray(row.product_names)
        ? (row.product_names.length > 0 ? row.product_names.join(", ") : "N/A")
        : (row.product_names ?? "N/A")
  },
  {
    id: "order_amount",
    label: "Order Amount",
    render: (row) =>
      row.order_amount != null
        ? `$ ${Number(row.order_amount).toLocaleString()}`
        : "N/A"
  },
  {
    id: "bude_order_amount",
    label: "Bud-i Amount",
    render: (row) =>
      row.bude_order_amount != null
        ? `$ ${Number(row.bude_order_amount).toLocaleString()}`
        : "N/A"
  },
  {
    id: "store_name",
    label: "Store Name",
    render: (row) => row.store_name ?? "N/A"
  },
  {
    id: "order_date",
    label: "Order Date",
    render: (row) => {
      if (!row.order_date) return "N/A";
      const d = new Date(row.order_date);
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const year = d.getFullYear();
      return `${month}-${day}-${year}`;
    }
  },
  {
    id: "product_category",
    label: "Product Category",
    render: (row) => row.product_category ?? "N/A"
  }
];


function OrdersList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ordersData, setOrdersData] = useState([]);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");

  // Get client_id from localStorage (adjust based on your auth implementation)
  const clientId = config.clientId || "0486bc6d-6d15-4ea3-8504-adefe1512324";

  useEffect(() => {
    const init = async () => {
      try {
        const response = await fetch(`${config.API_BASE_URL}/client/store?client_id=${clientId}`, {
          headers: config.getHeaders()
        });
        const result = await response.json();

        if (result.success && result.data && result.data.length > 0) {
          // Filter to active stores
          const activeStores = result.data.filter(s => s.status === "active");
          setStores(activeStores);

          if (activeStores.length > 0) {
            setSelectedStoreId(activeStores[0].client_store_id);
          }
        }
      } catch (err) {
        console.warn("Could not load stores", err);
      }

      fetchOrdersData();
    };
    init();
  }, [clientId]);

  const fetchOrdersData = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log("🔍 Fetching orders data for client:", clientId);

      const response = await fetch(
        `${config.API_BASE_URL}/client/revenue/list?client_id=${clientId}`,
        {
          method: "GET",
          headers: config.getHeaders()
        }
      );

      const result = await response.json();
      console.log("📊 Orders API response:", result);

      if (result.success && result.data) {
        setOrdersData(result.data);
        console.log(`✅ Loaded ${result.data.length} orders records`);
      } else {
        setError(result.message || "Failed to fetch orders data");
        console.error("❌ Orders fetch failed:", result.message);
      }

    } catch (err) {
      console.error("❌ Error fetching orders data:", err);
      setError("Failed to fetch orders data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSyncGa4 = async () => {
    if (!selectedStoreId) return;
    
    setSyncing(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/client/revenue/sync-ga4`, {
        method: "POST",
        headers: { ...config.getHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          client_id: clientId,
          client_store_id: selectedStoreId,
          days_back: 30 // For on-demand manual syncs, look back 30 days
        })
      });
      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: result.message, severity: "success" });
        await fetchOrdersData(); // Refresh the table
      } else {
        setSnackbar({ open: true, message: result.message || "Failed to sync GA4 data", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Error communicating with server for GA4 sync.", severity: "error" });
    } finally {
      setSyncing(false);
    }
  };

  const handleConfigureGa4 = () => {
    navigate("/orders/configure-ga4");
  };

  const customHeaderButtons = [
    {
      label: "Configure GA4",
      icon: "settings", // You can use any valid icon string your GenericTable supports
      onClick: handleConfigureGa4,
      disabled: false
    }
  ];

  const selectedStore = stores.find(s => s.client_store_id === selectedStoreId);

  // Filter orders by the selected store
  const filteredOrders = ordersData.filter(order => {
    if (!selectedStore) return false;
    return order.store_name === selectedStore.store_name;
  });

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ width: "100%", pt: 2 }}>
      {/* Top Control Bar matching Dashboard Theme */}
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          mb: 3,
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          backgroundColor: '#fff'
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Active Store:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <Select
              value={selectedStoreId}
              onChange={(e) => setSelectedStoreId(e.target.value)}
              displayEmpty
              sx={{
                borderRadius: '8px',
                backgroundColor: "#f8f9fa",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#b0b0b0" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1976d2" },
                fontWeight: 500
              }}
            >
              {stores.map((store) => (
                <MenuItem key={store.client_store_id} value={store.client_store_id}>
                  {store.store_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {selectedStore && selectedStore.ga4_property_id && (
          <Button
            variant="contained"
            color="primary"
            startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            onClick={handleSyncGa4}
            disabled={syncing || loading}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            {syncing ? "Syncing..." : "Sync GA4 Revenue"}
          </Button>
        )}
      </Paper>

      {selectedStore && !selectedStore.ga4_property_id ? (
        <div style={{ maxWidth: '650px', margin: '40px auto', padding: '35px', backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.12)', textAlign: 'center' }}>
          <h2 style={{ marginTop: 0, color: '#1a1a1a', fontSize: '24px' }}>Analytics Not Configured</h2>
          <p style={{ color: '#555', fontSize: '16px', lineHeight: '1.5', marginBottom: '30px' }}>
            Analytics are not configured for <strong>{selectedStore.store_name}</strong>. Please configure Google Analytics to view automated revenue tracking.
          </p>
          <button
            onClick={handleConfigureGa4}
            style={{
              padding: '14px 28px', background: '#1976d2', color: 'white',
              border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold',
              cursor: 'pointer', transition: 'background 0.2s'
            }}
          >
            Configure GA4
          </button>
        </div>
      ) : (
        <GenericTable
          title={`Recent Orders`}
          columns={ordersColumns}
          rows={filteredOrders}
          showView={false}
          showEdit={false}
          showDelete={false}
          dropdownFilters={["product_category"]}
          dateFilters={["order_date"]}
          rangeFilters={["order_amount"]}
          customHeaderButtons={customHeaderButtons}
          onView={(row) => alert(`View user: ${row.username}`)}
          onEdit={(row) => alert(`Edit user: ${row.username}`)}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default OrdersList;
